import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  View,
  Text,
  FlatList,
  Pressable,
  ActivityIndicator,
  TextInput,
  RefreshControl,
  ScrollView,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { noOutlineStyle, isWeb } from '@/lib/platform';
import {
  Search,
  Rocket,
  TrendingUp,
  Shield,
  CheckCircle2,
  Clock,
  Users2,
  X,
  ChevronRight,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAdminUsers } from '@/lib/admin';
import type { AdminUserItem } from '@/lib/admin';
import { mode } from '@/lib/config/app-mode';
import { hapticLight, hapticSelection } from '@/lib/haptics';
import { AppLayout } from '@/components/AppLayout';
import { WebCard } from '@/components/web/WebAppShell';

type RoleFilter = 'all' | 'startup' | 'investor';

const ROLE_FILTERS: { key: RoleFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'startup', label: 'Startups' },
  { key: 'investor', label: 'Investors' },
];

const ROLE_ICON: Record<string, typeof Rocket> = {
  startup: Rocket,
  investor: TrendingUp,
  admin: Shield,
};

const ROLE_COLOR: Record<string, string> = {
  startup: '#34C759',
  investor: '#007AFF',
  admin: '#FF9500',
};


export default function AdminUsersScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: users,
    isLoading,
    refetch,
    isRefetching,
  } = useAdminUsers({
    role: roleFilter === 'all' ? undefined : roleFilter,
    search: debouncedSearch || undefined,
  });

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleUserPress = (user: AdminUserItem) => {
    hapticLight();
    router.push(`/(admin)/user-detail?id=${user.id}`);
  };

  const renderItem = useCallback(
    ({ item, index }: { item: AdminUserItem; index: number }) => {
      const RoleIcon = ROLE_ICON[item.role] ?? Shield;
      const roleColor = ROLE_COLOR[item.role] ?? '#8E8E93';

      return (
        <Animated.View entering={FadeInDown.delay(index * 50).duration(400)}>
          <AnimatedPressable
            onPress={() => handleUserPress(item)}
            className="mb-3"
          >
            <WebCard>
              <View className="flex-row items-center justify-between">
                <View className="flex-1 mr-3">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-sm font-semibold"
                    numberOfLines={1}
                  >
                    {item.email}
                  </Text>
                  <View className="flex-row items-center gap-3 mt-1.5">
                    {/* Role badge */}
                    <View
                      className="flex-row items-center gap-1.5 px-2.5 py-1 rounded-lg"
                      style={{ backgroundColor: `${roleColor}15` }}
                    >
                      <RoleIcon size={12} color={roleColor} />
                      <Text style={{ color: roleColor }} className="text-xs font-bold capitalize">
                        {item.role}
                      </Text>
                    </View>

                    {/* Onboarding status */}
                    <View className="flex-row items-center gap-1">
                      {item.onboarding_completed ? (
                        <CheckCircle2 size={12} color="#34C759" />
                      ) : (
                        <Clock size={12} color="#FF9500" />
                      )}
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">
                        {item.onboarding_completed ? 'Onboarded' : 'Pending'}
                      </Text>
                    </View>

                  </View>

                </View>

                <View className="items-end gap-1">
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    {formatDate(item.created_at)}
                  </Text>
                  <ChevronRight size={14} color={colors.text.tertiary} />
                </View>
              </View>
            </WebCard>
          </AnimatedPressable>
        </Animated.View>
      );
    },
    [colors]
  );

  if (!isWeb) {
    return (
      <MobileUsersScreen
        users={users}
        isLoading={isLoading}
        isRefetching={isRefetching}
        refetch={refetch}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        search={search}
        setSearch={setSearch}
        onUserPress={handleUserPress}
        formatDate={formatDate}
        colors={colors}
      />
    );
  }

  return (
    <AppLayout
      title="Users"
      breadcrumbs={[
        { label: 'Admin', href: '/(admin)/admin-dashboard' },
        { label: 'Users' },
      ]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['bottom']}>
        {/* Header */}
        <View className="px-1 pt-2 pb-2">
          <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
            Users
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
            {users?.length ?? 0} accounts
          </Text>
        </View>

        {/* Search */}
        <View className="pb-3">
          <View
            className="flex-row items-center rounded-xl px-4"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <Search size={18} color={colors.text.tertiary} />
            <TextInput
              style={noOutlineStyle({ color: colors.text.primary })}
              className="flex-1 py-3 pl-3 text-sm"
              placeholder="Search by email..."
              placeholderTextColor={colors.text.tertiary}
              value={search}
              onChangeText={setSearch}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {search.length > 0 && (
              <Pressable onPress={() => setSearch('')} className="p-1">
                <X size={16} color={colors.text.tertiary} />
              </Pressable>
            )}
          </View>
        </View>

        {/* Role filter tabs */}
        <View style={{ paddingBottom: 10 }}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flexGrow: 0 }}
            contentContainerStyle={{ paddingHorizontal: 0, gap: 8 }}
          >
            {ROLE_FILTERS.map((rf) => {
              const active = roleFilter === rf.key;
              return (
                <AnimatedPressable
                  key={rf.key}
                  onPress={() => {
                    hapticSelection();
                    setRoleFilter(rf.key);
                  }}
                  className="px-4 rounded-full items-center justify-center"
                  style={{
                    backgroundColor: active ? colors.primary[600] : colors.surface.primary,
                    height: 32,
                  }}
                >
                  <Text
                    style={{ color: active ? '#FFFFFF' : colors.text.secondary }}
                    className="text-xs font-semibold"
                  >
                    {rf.label}
                  </Text>
                </AnimatedPressable>
              );
            })}
          </ScrollView>
        </View>

        {/* Divider */}
        <View style={{ height: 1, backgroundColor: colors.border.light, marginBottom: 8 }} />

        {/* List */}
        {isLoading ? (
          <View className="flex-1 items-center justify-center">
            <ActivityIndicator size="large" color={colors.primary[600]} />
          </View>
        ) : !users?.length ? (
          <View className="flex-1 items-center justify-center px-8">
            <Users2 size={48} color={colors.text.tertiary} />
            <Text style={{ color: colors.text.secondary }} className="text-base font-medium mt-4 text-center">
              No users found
            </Text>
          </View>
        ) : (
          <FlatList
            data={users}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            maxToRenderPerBatch={10}
            windowSize={5}
            contentContainerStyle={{ paddingTop: 4, paddingBottom: 24 }}
            refreshControl={
              <RefreshControl
                refreshing={isRefetching}
                onRefresh={refetch}
                tintColor={colors.primary[600]}
              />
            }
          />
        )}
      </SafeAreaView>
    </AppLayout>
  );
}

// ─── Mobile Layout ────────────────────────────────────────────────────────────

interface MobileUsersScreenProps {
  users: AdminUserItem[] | undefined;
  isLoading: boolean;
  isRefetching: boolean;
  refetch: () => void;
  roleFilter: RoleFilter;
  setRoleFilter: (v: RoleFilter) => void;
  search: string;
  setSearch: (v: string) => void;
  onUserPress: (user: AdminUserItem) => void;
  formatDate: (iso: string) => string;
  colors: ReturnType<typeof useThemeColors>;
}

function MobileUsersScreen({
  users,
  isLoading,
  isRefetching,
  refetch,
  roleFilter,
  setRoleFilter,
  search,
  setSearch,
  onUserPress,
  formatDate,
  colors,
}: MobileUsersScreenProps) {
  const formatDateCompact = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderItem = useCallback(
    ({ item, index }: { item: AdminUserItem; index: number }) => {
      const RoleIcon = ROLE_ICON[item.role] ?? Shield;
      const roleColor = ROLE_COLOR[item.role] ?? '#8E8E93';

      return (
        <Animated.View entering={FadeInDown.delay(index * 50).duration(400)}>
          <AnimatedPressable
            onPress={() => onUserPress(item)}
            className="mb-2"
          >
            <View
              className="px-3 py-2.5 rounded-xl"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <View className="flex-row items-center justify-between">
                <View className="flex-1 mr-2">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-sm font-semibold"
                    numberOfLines={1}
                  >
                    {item.email}
                  </Text>
                  <View className="flex-row items-center gap-2 mt-1">
                    {/* Role badge — compact */}
                    <View
                      className="flex-row items-center gap-1 px-2 py-0.5 rounded-md"
                      style={{ backgroundColor: `${roleColor}15` }}
                    >
                      <RoleIcon size={11} color={roleColor} />
                      <Text style={{ color: roleColor }} className="text-xs font-bold capitalize" numberOfLines={1}>
                        {item.role}
                      </Text>
                    </View>

                    {/* Onboarding status */}
                    <View className="flex-row items-center gap-1">
                      {item.onboarding_completed ? (
                        <CheckCircle2 size={11} color="#34C759" />
                      ) : (
                        <Clock size={11} color="#FF9500" />
                      )}
                      <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                        {item.onboarding_completed ? 'Onboarded' : 'Pending'}
                      </Text>
                    </View>

                  </View>

                </View>

                <View className="items-end gap-0.5">
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    {formatDateCompact(item.created_at)}
                  </Text>
                  <ChevronRight size={13} color={colors.text.tertiary} />
                </View>
              </View>
            </View>
          </AnimatedPressable>
        </Animated.View>
      );
    },
    [colors, onUserPress]
  );

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['top', 'bottom']}>
      {/* Header */}
      <View className="px-4 pt-4 pb-2">
        <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
          Users
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm mt-0.5">
          {users?.length ?? 0} accounts
        </Text>
      </View>

      {/* Search */}
      <View className="px-4 pb-2">
        <View
          className="flex-row items-center rounded-xl px-3"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <Search size={16} color={colors.text.tertiary} />
          <TextInput
            style={noOutlineStyle({ color: colors.text.primary })}
            className="flex-1 py-2.5 pl-2.5 text-sm"
            placeholder="Search by email..."
            placeholderTextColor={colors.text.tertiary}
            value={search}
            onChangeText={setSearch}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {search.length > 0 && (
            <Pressable onPress={() => setSearch('')} className="p-1">
              <X size={14} color={colors.text.tertiary} />
            </Pressable>
          )}
        </View>
      </View>

      {/* Role filter pills */}
      <View style={{ paddingBottom: 8 }}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flexGrow: 0 }}
          contentContainerStyle={{ paddingHorizontal: 16, gap: 6 }}
        >
          {ROLE_FILTERS.map((rf) => {
            const active = roleFilter === rf.key;
            return (
              <AnimatedPressable
                key={rf.key}
                onPress={() => {
                  hapticSelection();
                  setRoleFilter(rf.key);
                }}
                className="px-3 rounded-full items-center justify-center"
                style={{
                  backgroundColor: active ? colors.primary[600] : colors.surface.primary,
                  height: 32,
                }}
              >
                <Text
                  style={{ color: active ? '#FFFFFF' : colors.text.secondary }}
                  className="text-xs font-semibold"
                  numberOfLines={1}
                >
                  {rf.label}
                </Text>
              </AnimatedPressable>
            );
          })}
        </ScrollView>
      </View>

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: colors.border.light, marginBottom: 6 }} />

      {/* List */}
      {isLoading ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary[600]} />
        </View>
      ) : !users?.length ? (
        <View className="flex-1 items-center justify-center px-8">
          <Users2 size={40} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mt-3 text-center">
            No users found
          </Text>
        </View>
      ) : (
        <FlatList
          data={users}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          maxToRenderPerBatch={10}
          windowSize={5}
          contentContainerStyle={{ paddingTop: 2, paddingBottom: 24 }}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={refetch}
              tintColor={colors.primary[600]}
            />
          }
        />
      )}
    </SafeAreaView>
  );
}
